import { Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  LayoutDashboard,
  Users,
  MessageSquare,
  FileText,
  BookOpen,
  Phone,
  Search,
  Activity,
  UserCog,
  Volume2,
  UserCircle,
  UsersRound,
  X,
  Gauge,
  Mail,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/store/authStore'
import { useUIStore } from '@/store/uiStore'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { leadsApi } from '@/api/endpoints/leads'
import { emailQueueApi } from '@/api/endpoints/queue'

interface NavItem {
  title: string
  href: string
  icon: React.ElementType
  adminOnly?: boolean
  managerAccess?: boolean  // Accessible to sales_manager role too
}

const mainNavItems: NavItem[] = [
  { title: 'Dashboard', href: '/', icon: LayoutDashboard },
  { title: 'Leads', href: '/leads', icon: Users },
  { title: 'Activities', href: '/activities', icon: Activity },
  { title: 'Conversations', href: '/conversations', icon: MessageSquare },
]

const configNavItems: NavItem[] = [
  { title: 'Prompts', href: '/prompts', icon: FileText },
  { title: 'Email Templates', href: '/email-templates', icon: Mail },
  { title: 'Knowledge Base', href: '/knowledge', icon: BookOpen },
  { title: 'Inbound Numbers', href: '/inbound', icon: Phone },
  { title: 'Voices', href: '/voices', icon: Volume2 },
  { title: 'Engagement', href: '/engagement', icon: UserCircle },
  { title: 'Engagement Batch', href: '/engagement-batch', icon: Gauge, managerAccess: true },
  { title: 'Agent Pool', href: '/agent-pool', icon: UsersRound },
  { title: 'Scout', href: '/scout', icon: Search },
]

const adminNavItems: NavItem[] = [
  { title: 'User Management', href: '/users', icon: UserCog, adminOnly: true },
]

export function Sidebar() {
  const location = useLocation()
  const { isAdmin, isSalesManager, authEnabled } = useAuthStore()
  const { sidebarOpen, setSidebarOpen, sidebarCollapsed } = useUIStore()

  // Fetch pending review counts for badges
  const { data: leadReviewStats } = useQuery({
    queryKey: ['leads-review-stats-sidebar'],
    queryFn: () => leadsApi.getReviewStats().then(res => res.data),
    staleTime: 30000,
    refetchInterval: 60000,
  })

  const { data: emailQueueStats } = useQuery({
    queryKey: ['email-queue-stats-sidebar'],
    queryFn: () => emailQueueApi.getStats().then(res => res.data),
    staleTime: 30000,
    refetchInterval: 60000,
  })

  const leadsPendingReview = leadReviewStats?.pending_review || 0
  const emailsPendingReview = emailQueueStats?.pending_review || 0

  // Show admin items if user is admin OR if auth is disabled (for local development)
  const showAdminItems = isAdmin || !authEnabled
  // Sales managers can see items marked with managerAccess
  const showManagerItems = isSalesManager

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/'
    return location.pathname.startsWith(href)
  }

  const getBadgeCount = (href: string): number => {
    if (href === '/leads') return leadsPendingReview
    if (href === '/conversations') return emailsPendingReview
    return 0
  }

  const NavLink = ({ item }: { item: NavItem }) => {
    const badgeCount = getBadgeCount(item.href)
    return (
      <Link
        to={item.href}
        onClick={() => setSidebarOpen(false)}
        className={cn(
          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all hover:bg-primary-800',
          isActive(item.href)
            ? 'bg-primary-800 text-white'
            : 'text-blue-100 hover:text-white'
        )}
      >
        <item.icon className="h-5 w-5" />
        {!sidebarCollapsed && (
          <span className="flex-1">{item.title}</span>
        )}
        {badgeCount > 0 && (
          <Badge className="bg-orange-500 text-white text-xs px-1.5 py-0 h-5 min-w-[20px] flex items-center justify-center">
            {badgeCount > 99 ? '99+' : badgeCount}
          </Badge>
        )}
      </Link>
    )
  }

  const sidebarContent = (
    <div className="flex h-full flex-col bg-primary-900 text-white">
      {/* Logo */}
      <div className="flex h-16 items-center justify-between px-4">
        <Link to="/" className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white">
            <span className="text-lg font-bold text-primary-900">R</span>
          </div>
          {!sidebarCollapsed && (
            <span className="text-lg font-semibold">Riviera AI</span>
          )}
        </Link>
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden text-white hover:bg-primary-800"
          onClick={() => setSidebarOpen(false)}
        >
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 px-2 py-4">
        {/* Main Navigation */}
        <div className="space-y-1">
          {mainNavItems.map((item) => (
            <NavLink key={item.href} item={item} />
          ))}
        </div>

        {/* Configuration Section - Admin only (with manager exceptions) */}
        {showAdminItems && (
          <div className="pt-4">
            <Separator className="bg-primary-700 mb-4" />
            {!sidebarCollapsed && (
              <p className="px-3 text-xs font-semibold uppercase tracking-wider text-blue-300 mb-2">
                Configuration
              </p>
            )}
            <div className="space-y-1">
              {configNavItems.map((item) => (
                <NavLink key={item.href} item={item} />
              ))}
            </div>
          </div>
        )}

        {/* Sales Manager - Show only manager-accessible items */}
        {!showAdminItems && showManagerItems && (
          <div className="pt-4">
            <Separator className="bg-primary-700 mb-4" />
            {!sidebarCollapsed && (
              <p className="px-3 text-xs font-semibold uppercase tracking-wider text-blue-300 mb-2">
                Monitoring
              </p>
            )}
            <div className="space-y-1">
              {configNavItems.filter(item => item.managerAccess).map((item) => (
                <NavLink key={item.href} item={item} />
              ))}
            </div>
          </div>
        )}

        {/* User Management Section - Admin only */}
        {showAdminItems && (
          <div className="pt-4">
            <Separator className="bg-primary-700 mb-4" />
            {!sidebarCollapsed && (
              <p className="px-3 text-xs font-semibold uppercase tracking-wider text-blue-300 mb-2">
                Admin
              </p>
            )}
            <div className="space-y-1">
              {adminNavItems.map((item) => (
                <NavLink key={item.href} item={item} />
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Footer */}
      <div className="border-t border-primary-700 p-4">
        <div className="flex items-center gap-2 text-xs text-blue-300">
          <div className="h-2 w-2 rounded-full bg-green-400" />
          <span>System Online</span>
        </div>
      </div>
    </div>
  )

  return (
    <>
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out lg:hidden',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {sidebarContent}
      </aside>

      {/* Desktop sidebar */}
      <aside
        className={cn(
          'hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-40 lg:block lg:transition-all lg:duration-300',
          sidebarCollapsed ? 'lg:w-16' : 'lg:w-64'
        )}
      >
        {sidebarContent}
      </aside>
    </>
  )
}
